import React from "react";
import { SelectProps } from "@mantine/core";

import { DRAWER_COMBOBOX_PROPS } from "@/utils/mantineUiHelpers";

import { AppIcon } from "../../generic/AppIcon";
import { SelectField } from "@/components/generic/form";

const STATUS_OPTIONS = [
  {
    value: "100",
    label: "Enabled",
    icon: "Check",
    color: "green",
    description: "Active and visible",
  },
  {
    value: "0",
    label: "Disabled",
    icon: "X",
    color: "red",
    description: "Inactive and hidden",
  },
] as const;

export type StatusValue = 100 | 0;

export interface StatusSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: number | null;

  /** Callback when status changes */
  onChange: (value: number | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const StatusSelector: React.FC<StatusSelectorProps> = ({
  value,
  onChange,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select status",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = STATUS_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      const numValue = Number(stringValue) as StatusValue;
      onChange(numValue);
    }
  };

  // Get current option for icon display
  const currentOption = STATUS_OPTIONS.find((opt) => opt.value === stringValue);
  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color:
            currentOption.color === "green"
              ? "var(--mantine-color-green-6)"
              : "var(--mantine-color-red-6)",
        }}
      />
    ) : undefined;

  return (
    <SelectField
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={(_name, v) => handleChange(v)}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
      size="md"
      comboboxProps={DRAWER_COMBOBOX_PROPS}
    />
  );
};

// Export status options for use in other components
export { STATUS_OPTIONS };
